import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from './client.js';
import type { Membership } from './types.js';

export interface UserLookup {
  uid: string;
  email: string | null;
  displayName: string | null;
}

const HOUR = 60 * 60 * 1000;

export function useTenantMembers(tenantId: string) {
  return useQuery<Membership[]>({
    queryKey: ['tenant-members', tenantId],
    queryFn: () => apiFetch<Membership[]>(`/tenants/${encodeURIComponent(tenantId)}/members`),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}

/**
 * Resolve an email → user record via `GET /api/users/by-email`. Returns
 * null on 404 (caller checks `query.data === null` to surface "Aucun
 * utilisateur trouvé"). Email normalised to lowercase before query so the
 * cache hit-rate is decent across case variants.
 */
export function useUserByEmail(email: string) {
  const normalized = email.trim().toLowerCase();
  return useQuery<UserLookup | null>({
    queryKey: ['user-by-email', normalized],
    queryFn: async () => {
      if (!normalized) return null;
      try {
        return await apiFetch<UserLookup>(
          `/users/by-email?email=${encodeURIComponent(normalized)}`,
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: normalized.length > 3 && normalized.includes('@'),
    staleTime: 60_000,
  });
}

/**
 * Resolve a Firebase uid → user record via `GET /api/users/by-uid`. Cached
 * for 1h since display names rarely change. Returns null on 404 so callers
 * can fall back to the raw uid without crashing.
 */
export function useUserByUid(uid: string | null | undefined) {
  return useQuery<UserLookup | null>({
    queryKey: ['user-by-uid', uid ?? null],
    queryFn: async () => {
      if (!uid) return null;
      try {
        return await apiFetch<UserLookup>(`/users/by-uid?uid=${encodeURIComponent(uid)}`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: uid != null,
    staleTime: HOUR,
    gcTime: HOUR * 24,
  });
}

/**
 * Prefetch a batch of uid → user records into the react-query cache so that
 * subsequent `useUserByUid` calls for the same uids resolve instantly.
 */
export function usePrefetchUsers(uids: string[]) {
  const qc = useQueryClient();
  return () => {
    for (const uid of uids) {
      void qc.prefetchQuery({
        queryKey: ['user-by-uid', uid],
        queryFn: () => apiFetch<UserLookup>(`/users/by-uid?uid=${encodeURIComponent(uid)}`),
        staleTime: HOUR,
      });
    }
  };
}
